import { Card } from "@amzn/stencil-react-components/card";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { TextArea } from "@amzn/stencil-react-components/text-area";
import Button, { ButtonVariant } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import { CommentBubble } from "./CommentBubble";
import { ReplyForm } from "./ReplyForm";
interface Comment {
  id: string;
  authorAlias: string;
  authorName: string;
  date: string;
  text: string;
  replies?: Comment[];
}

export interface UseCaseDiscussionProps {
  comments: Comment[];
  useCaseId?: string;
  onPostComment?: (text: string) => Promise<void>;
  onPostReply?: (text: string, parentCommentId: string) => Promise<void>;
}

export const UseCaseDiscussion: FC<UseCaseDiscussionProps> = ({ comments, useCaseId, onPostComment, onPostReply }) => {
  const [commentText, setCommentText] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const replyRef = useRef<HTMLTextAreaElement>(null);

  const handleReplyClick = useCallback((commentId: string) => {
    setReplyingTo(commentId);
    setReplyText("");
  }, []);

  // Focus textarea when reply form appears
  useEffect(() => {
    if (replyingTo) replyRef.current?.focus();
  }, [replyingTo]);

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
    setReplyText("");
  }, []);

  const handlePostReply = useCallback(async () => {
    if (!replyingTo || !replyText.trim() || !onPostReply) return;
    setIsPosting(true);
    setPostError(null);
    try {
      await onPostReply(replyText.trim(), replyingTo);
      setReplyingTo(null);
      setReplyText("");
    } catch {
      setPostError(getString("useCases.discussion.postError"));
    } finally {
      setIsPosting(false);
    }
  }, [replyText, replyingTo, onPostReply]);

  const handlePostComment = useCallback(async () => {
    if (!commentText.trim() || !onPostComment) return;
    setIsPosting(true);
    setPostError(null);
    try {
      await onPostComment(commentText.trim());
      setCommentText("");
    } catch {
      setPostError(getString("useCases.discussion.postError"));
    } finally {
      setIsPosting(false);
    }
  }, [commentText, onPostComment]);

  return (
    <Col gridGap="dimensions.spacing.400" width="100%">
      <Card padding="dimensions.spacing.400" width="100%">
        <Col gridGap="dimensions.spacing.300" width="100%">
          <TextArea
            placeholder={getString("useCases.discussion.placeholder")}
            aria-label={getString("useCases.discussion.placeholder")}
            value={commentText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCommentText(e.target.value)}
            resize="vertical"
            width="100%"
            minHeight={100}
          />
          <Row>
            <Button
              variant={ButtonVariant.Primary}
              disabled={!commentText.trim() || isPosting}
              onClick={handlePostComment}
              touchpoint="usecase-post-comment"
              customData={{ useCaseId }}
            >
              {getString("useCases.discussion.postComment")}
            </Button>
          </Row>
          {postError && (
            <Row>
              <Text fontSize="T100" color="color.text.error">
                {postError}
              </Text>
            </Row>
          )}
        </Col>
      </Card>

      <Col gridGap="dimensions.spacing.400" role="list" aria-label={getString("useCases.discussion.commentsLabel")}>
        {comments.map((comment) => (
          <Col key={comment.id} gridGap="dimensions.spacing.200" role="listitem">
            <CommentBubble
              authorAlias={comment.authorAlias}
              authorName={comment.authorName}
              date={comment.date}
              text={comment.text}
              actions={
                <>
                  <Row>
                    <Button
                      variant={ButtonVariant.Tertiary}
                      touchpoint="usecase-reply"
                      customData={{ commentId: comment.id, useCaseId }}
                      onClick={() => handleReplyClick(comment.id)}
                    >
                      {getString("useCases.discussion.reply")}
                    </Button>
                  </Row>
                  {replyingTo === comment.id && (
                    <ReplyForm
                      ref={replyRef}
                      value={replyText}
                      onChange={setReplyText}
                      onPost={handlePostReply}
                      onCancel={handleCancelReply}
                      commentId={comment.id}
                      useCaseId={useCaseId}
                    />
                  )}
                </>
              }
            />

            {comment.replies?.map((reply) => (
              <View
                key={reply.id}
                margin={{ left: "48px" }}
                padding={{ left: "dimensions.spacing.300" }}
                style={{ borderLeft: "2px solid var(--st-dt-color-neutral-30)" }}
              >
                <CommentBubble
                  authorAlias={reply.authorAlias}
                  authorName={reply.authorName}
                  date={reply.date}
                  text={reply.text}
                />
              </View>
            ))}
          </Col>
        ))}
      </Col>
    </Col>
  );
};

export const getCommentCount = (comments: Comment[]): number =>
  comments.reduce((n, c) => n + 1 + (c.replies?.length ?? 0), 0);
